import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Medicines from "@/pages/Medicines";
import Categories from "@/pages/Categories";
import StoreTrack from "@/pages/store/TrackOrder";
import ImportPage from "@/pages/ImportPage";
import Invoices from "@/pages/Invoices";
import Customers from "@/pages/Customers";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import AddMedicine from "@/pages/AddMedicine";
import AddStock from "@/pages/AddStock";
import Suppliers from "@/pages/Suppliers";
import SalesReport from "@/pages/SalesReport";
import ProfitLoss from "@/pages/ProfitLoss";
import ExpireSoon from "@/pages/ExpireSoon";
import Staff from "@/pages/Staff";
import Credits from "@/pages/Credits";
import Closing from "@/pages/Closing";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Doctors from "@/pages/Doctors";
import OnlineOrders from "@/pages/OnlineOrders";
import Branches from "@/pages/Branches";
import Attendance from "@/pages/Attendance";
import Returns from "@/pages/Returns";
import CAReport from "@/pages/CAReport";
import Offers from "@/pages/Offers";
import Reviews from "@/pages/Reviews";
import StoreOffers from "@/pages/store/Offers";
import StoreBlog from "@/pages/store/Blog";
import StoreBlogPost from "@/pages/store/BlogPost";
import BlogManager from "@/pages/BlogManager";
import ChatLogs from "@/pages/ChatLogs";
import { CartProvider } from "@/storefront/CartContext";
import StoreLayout from "@/storefront/StoreLayout";
import StoreHome from "@/pages/store/Home";
import StoreShop from "@/pages/store/Shop";
import StoreMedicine from "@/pages/store/Medicine";
import StoreCart from "@/pages/store/Cart";
import StoreAbout from "@/pages/store/About";
import StoreContact from "@/pages/store/Contact";
import StoreUploadRx from "@/pages/store/UploadRx";
import { Loader2 } from "lucide-react";

function FullScreenLoader({ label = "Loading Trustmeds Pharma…" }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-emerald-900">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="font-display font-semibold">{label}</span>
      </div>
    </div>
  );
}

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    api
      .post("/auth/google", null, { headers: { "X-Session-ID": sessionId } })
      .then(({ data }) => {
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.user?.role === "hr" ? "/attendance" : "/pos", { replace: true, state: { user: data.user } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate, setUser]);

  return <FullScreenLoader label="Signing you in with Google…" />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

const homeFor = (role) => (role === "hr" ? "/attendance" : role === "ca" ? "/ca-report" : "/pos");

function NotFound() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4 text-center" data-testid="not-found-page">
      <div className="font-display text-6xl font-extrabold text-emerald-900">404</div>
      <p className="mt-3 text-slate-600 text-sm">Ye page exist nahi karta — ho sakta hai link purana ho.</p>
      <a href="/" data-testid="notfound-home-link" className="mt-6 inline-flex items-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white font-semibold px-6 py-3 rounded-full text-sm">Homepage par jayein</a>
    </div>
  );
}

function RoleProtected({ roles, children }) {
  const { user } = useAuth();
  if (user && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — synchronous, prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Public><Login /></Public>} />
      <Route path="/" element={<CartProvider><StoreLayout /></CartProvider>}>
        <Route index element={<StoreHome />} />
      </Route>
      <Route path="/store" element={<CartProvider><StoreLayout /></CartProvider>}>
        <Route index element={<Navigate to="/" replace />} />
        <Route path="shop" element={<StoreShop />} />
        <Route path="medicine/:id" element={<StoreMedicine />} />
        <Route path="track" element={<StoreTrack />} />
        <Route path="cart" element={<StoreCart />} />
        <Route path="about" element={<StoreAbout />} />
        <Route path="contact" element={<StoreContact />} />
        <Route path="upload-rx" element={<StoreUploadRx />} />
        <Route path="offers" element={<StoreOffers />} />
        <Route path="blog" element={<StoreBlog />} />
        <Route path="blog/:slug" element={<StoreBlogPost />} />
      </Route>
      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="pos" element={<RoleProtected roles={["admin", "manager", "agent"]}><POS /></RoleProtected>} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="medicines" element={<RoleProtected roles={["admin", "manager"]}><Medicines /></RoleProtected>} />
        <Route path="categories" element={<RoleProtected roles={["admin", "manager"]}><Categories /></RoleProtected>} />
        <Route path="import" element={<RoleProtected roles={["admin", "manager"]}><ImportPage /></RoleProtected>} />
        <Route path="invoices" element={<RoleProtected roles={["admin", "manager", "agent"]}><Invoices /></RoleProtected>} />
        <Route path="customers" element={<RoleProtected roles={["admin", "manager", "agent"]}><Customers /></RoleProtected>} />
        <Route path="reports" element={<RoleProtected roles={["admin", "manager"]}><Reports /></RoleProtected>} />
        <Route path="settings" element={<RoleProtected roles={["admin"]}><Settings /></RoleProtected>} />
        <Route path="add-medicine" element={<RoleProtected roles={["admin", "manager"]}><AddMedicine /></RoleProtected>} />
        <Route path="add-stock" element={<RoleProtected roles={["admin", "manager"]}><AddStock /></RoleProtected>} />
        <Route path="suppliers" element={<RoleProtected roles={["admin", "manager"]}><Suppliers /></RoleProtected>} />
        <Route path="sale-report" element={<RoleProtected roles={["admin", "manager"]}><SalesReport /></RoleProtected>} />
        <Route path="profit-loss" element={<RoleProtected roles={["admin", "manager"]}><ProfitLoss /></RoleProtected>} />
        <Route path="expire-soon" element={<RoleProtected roles={["admin", "manager"]}><ExpireSoon /></RoleProtected>} />
        <Route path="staff" element={<RoleProtected roles={["admin"]}><Staff /></RoleProtected>} />
        <Route path="credits" element={<RoleProtected roles={["admin", "manager"]}><Credits /></RoleProtected>} />
        <Route path="returns" element={<RoleProtected roles={["admin", "manager"]}><Returns /></RoleProtected>} />
        <Route path="closing" element={<RoleProtected roles={["admin", "manager"]}><Closing /></RoleProtected>} />
        <Route path="purchase-orders" element={<RoleProtected roles={["admin", "manager"]}><PurchaseOrders /></RoleProtected>} />
        <Route path="doctors" element={<RoleProtected roles={["admin", "manager"]}><Doctors /></RoleProtected>} />
        <Route path="online-orders" element={<RoleProtected roles={["admin", "manager", "agent"]}><OnlineOrders /></RoleProtected>} />
        <Route path="ca-report" element={<RoleProtected roles={["admin", "manager", "ca"]}><CAReport /></RoleProtected>} />
        <Route path="offers" element={<RoleProtected roles={["admin", "manager"]}><Offers /></RoleProtected>} />
        <Route path="reviews" element={<RoleProtected roles={["admin", "manager"]}><Reviews /></RoleProtected>} />
        <Route path="blog-manager" element={<RoleProtected roles={["admin", "manager"]}><BlogManager /></RoleProtected>} />
        <Route path="chat-logs" element={<RoleProtected roles={["admin", "manager"]}><ChatLogs /></RoleProtected>} />
        <Route path="branches" element={<RoleProtected roles={["admin"]}><Branches /></RoleProtected>} />
        <Route path="attendance" element={<RoleProtected roles={["admin", "manager", "hr"]}><Attendance /></RoleProtected>} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
